import React from "react";

import { AddBusinessRounded, CloseRounded, SaveRounded } from "@mui/icons-material";
import { Box, DialogContent } from "@mui/material";
import { httpsCallable } from "firebase/functions";
import IMask from "imask";

import { Constants } from "../../../../config/constants";
import useFirebase from "../../../../config/context/firebaseContext";
import useModal from "../../../../config/context/modalContext";
import { Dev } from "../../../../config/util/consoleUtil";

import SimpleBase64 from "../../../../components/InputImage/SimpleBase64";
import ChipMain from "../../../../components/InputMain/ChipMain";
import TextMain from "../../../../components/InputMain/TextMain";
import { WideDialog } from "../../../../components/MainModalSimple/styles";
import MainSolidButton from "../../../../components/MainSolidButton";
import MainText from "../../../../components/MainText";
import { ModalTransition } from "../../../../components/ModalTransition";
import PageHeader from "../../../../components/PageHeader";

import { ModalBaseType } from "../../../../types/common";
import { CafeteriaType, UserUploadedFileType } from "../../../../types/database";

type ModalWriteCafeteriaProps = ModalBaseType & {
  cafeteria?: CafeteriaType;
};

export default function ModalWriteCafeteria({ onClose, cafeteria }: ModalWriteCafeteriaProps) {
  const { pushAlert } = useModal();
  const { functions } = useFirebase();

  let phoneMask = IMask.createMask({ mask: "(00) 0 0000-0000" });
  phoneMask.resolve(cafeteria?.phone || "");

  const [open, setOpen] = React.useState(true);

  const [nameField, setNameField] = React.useState(cafeteria?.name || "");
  const [descriptionField, setDescriptionField] = React.useState(cafeteria?.description || "");
  const [phoneField, setPhoneField] = React.useState(phoneMask.value || "");
  const [phoneRawField, setRawPhoneField] = React.useState(phoneMask.unmaskedValue || "");
  const [locIdField, setLocIdField] = React.useState<string[]>(cafeteria?.locationId || []);
  const [imageField, setImageField] = React.useState<UserUploadedFileType[]>(
    cafeteria?.image ? [cafeteria?.image] : []
  );

  const [instagramField, setInstagramField] = React.useState(cafeteria?.instagram || "");
  const [siteField, setSiteField] = React.useState(cafeteria?.site || "");
  const [locationTextField, setLocationTextField] = React.useState(cafeteria?.locationText || "");

  const [loading, setLoading] = React.useState(false);

  const handleWrite = async () => {
    try {
      setLoading(true);
      const writeCafeteria = httpsCallable(functions, "writeCafeteria");

      const payload = {
        name: nameField,
        phone: phoneRawField,
        description: descriptionField,
        image: imageField[0],
        instagram: instagramField,
        site: siteField,
        locationText: locationTextField,
        locationId: locIdField,
      } as any;

      if (cafeteria) payload.id = cafeteria.id;
      const response = await writeCafeteria(payload);

      pushAlert({
        type: "success",
        message: (response?.data as any)?.message || "Informações atualizadas",
      });

      handleClose(true);
    } catch (error) {
      console.log(...new Dev("handleWrite", error).Error());
      pushAlert({
        type: "error",
        message: `Erro ao salvar dados`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (reload: boolean = false) => {
    setOpen(false);
    setTimeout(() => {
      if (onClose) onClose(reload);
    }, 200);
  };

  return (
    <WideDialog
      fullWidth={true}
      open={open}
      onClose={() => handleClose()}
      TransitionComponent={ModalTransition}
    >
      <DialogContent>
        <PageHeader
          size="subtitle"
          title={cafeteria ? "Editar" : "Criar Lanchonete"}
          rightIcon={<CloseRounded />}
          onRightAction={() => handleClose()}
        />

        <Box>
          <Box mt={4}>
            <TextMain
              name="Nome da Lanchonete*"
              value={nameField}
              onChange={setNameField}
              textColor={"regular"}
            />

            <TextMain
              name="Descrição*"
              value={descriptionField}
              onChange={setDescriptionField}
              textColor={"regular"}
            />

            <TextMain
              name="Telefone*"
              value={phoneField}
              onChange={(value) => {
                phoneMask.resolve(value);
                setPhoneField(phoneMask.value);
                setRawPhoneField(phoneMask.unmaskedValue);
              }}
              textColor={"regular"}
              small={"WhatsApp, Com DDD"}
              inputType="tel"
              inputMode="tel"
            />

            <TextMain
              inputType="url"
              name="Instagram"
              value={instagramField}
              onChange={setInstagramField}
              textColor={"regular"}
              small={"Somente o user, sem @"}
            />

            <TextMain
              name="Site"
              inputType="url"
              value={siteField}
              onChange={setSiteField}
              textColor={"regular"}
            />

            <TextMain
              name="Local de Retirada"
              value={locationTextField}
              onChange={setLocationTextField}
              textColor={"regular"}
            />

            <MainText size="body" color="regular">
              Locais Atendidos:
            </MainText>
            <Box mb={1}>
              <MainText size="small" color="regular">
                Os locais atendidos servirão apenas para filtragem dos usuários. Para adicionar os
                pontos de entrega vá para as configurações.
              </MainText>
            </Box>
            <Box display={"flex"} gap={1} flexWrap={"wrap"} mt={1} mb={2}>
              <ChipMain
                options={Constants.cafeteriaSpots}
                onChange={(value: any) => {
                  if (locIdField.includes(value))
                    setLocIdField(locIdField.filter((item) => item !== value));
                  else setLocIdField([...locIdField, value]);
                }}
                value={locIdField}
                multiple
              />
            </Box>
          </Box>

          <Box>
            <SimpleBase64
              imageHeight={120}
              fileList={imageField}
              setFileList={setImageField}
              isImage
            />
          </Box>

          <Box mt={3} mb={1}>
            <MainSolidButton
              loading={loading}
              text={cafeteria ? "Salvar" : "Criar"}
              icon={cafeteria ? <SaveRounded /> : <AddBusinessRounded />}
              onClick={handleWrite}
            />
          </Box>

          {!cafeteria && (
            <MainText size="tiny" color="regular">
              A sua lanchonete precisa ser aprovado para aparecer na listagem e começar a receber
              pedidos.
            </MainText>
          )}
        </Box>
      </DialogContent>
    </WideDialog>
  );
}
